import { useCallback, useMemo, useState } from "react"

// import dayjs from 'dayjs'
import { App, Button, Divider, Dropdown, Flex, Tooltip, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { VariableSizeList as List } from "react-window"

import Private from "../../Components/Private"
import Icon from "../../Icon"
import { modalConfig } from "../../theme"
import { statusMap, statusTextMap, tooltipTextMap } from "../../Utils/utils"
import DotsComponent from "./dotsComponent"

import "./EnrollmentDescriptorCard.less"

const { Text } = Typography

const options = [
  "in_progress",
  "active",
  "enrolled_elsewhere",
  "generic_descriptor",
  "banned_descriptor",
  "invalid_arns",
  "invalid_descriptor",
  "unenrolled",
  "needs_assistance",
  "pending",
  "pending_unenroll",
  "in_re_enrollment",
  "verification_needed"
]

const options_type = ["Exact match", "Starts with"]

const statusTooltipTextMap = {
  in_progress: "Enrolled",
  pending: "Enrolled",
  active: "Connected",
  failed: "Failed",
  unenrolled: "Unenrolled"
}

const EnrollmentDescriptorCard = ({
  enrollment,
  isAdmin,
  onChangeStatusDescriptor,
  onChangeDescriptorType
}) => {
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const { isMobile } = useSelector((state) => state.nav)

  // State for lazy load
  const [visibleCount, setVisibleCount] = useState(20)
  const [isLoading, setIsLoading] = useState(false)

  // filtered descriptors
  const filteredDescriptors = useMemo(() => {
    return (
      enrollment?.["billing_descriptors"]?.filter(({ billing_descriptor }) => billing_descriptor) ||
      []
    )
  }, [enrollment])

  // loadedDescriptors
  const loadedDescriptors = useMemo(() => {
    return filteredDescriptors.slice(0, visibleCount)
  }, [filteredDescriptors, visibleCount])

  // check if it has more descriptors
  const hasMore = visibleCount < filteredDescriptors.length

  const loadMore = useCallback(() => {
    setIsLoading(true)
    // Імітація затримки для демо (можна видалити)
    setTimeout(() => {
      setVisibleCount((prev) => Math.min(prev + 20, filteredDescriptors.length))
      setIsLoading(false)
    }, 300)
  }, [filteredDescriptors.length])

  const openModalConfirm = (data) => {
    modal.confirm({
      ...modalConfig("info"),
      centered: !isMobile,

      title: t("Billing Descriptor type"),
      content: (
        <Text type="secondary">
          {t(
            "Are you sure you want to change the billing descriptor type? The selected type will be automatically applied to this descriptor in all enrollments (Ethoca, RDR, CDRN), including those added later."
          )}
        </Text>
      ),
      cancelText: t("Go back"),
      okText: t("Change and apply"),
      cancelButtonProps: { size: "large" },
      okButtonProps: { size: "large" },
      onOk() {
        onChangeDescriptorType(data)
      }
    })
  }

  // row component in virtual list
  const DescriptorRow = ({ index, style }) => {
    const descriptor = loadedDescriptors[index]
    if (!descriptor) return null

    const { billing_descriptor, statuses_info, current_status, ...descriptorData } = descriptor

    if (isAdmin) {
      return (
        <div style={{ ...style, display: "flex", alignItems: "flex-start" }}>
          <div style={{ marginTop: 12, width: "100%" }}>
            <Flex gap={4}>
              <Text copyable={{ tooltips: false, text: billing_descriptor }} />
              <Text
                ellipsis={{ rows: 2 }}
                style={{
                  display: "block",
                  whiteSpace: "normal"
                }}
              >
                <Private>{billing_descriptor}</Private>
              </Text>
            </Flex>
            <Flex gap={8} align="center">
              <DotsComponent status={`${statusMap[current_status]}`} />
              <Dropdown
                overlayStyle={{
                  maxHeight: 200,
                  overflow: "auto",
                  borderRadius: 15,
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
                }}
                overlayClassName="custom-dropdown"
                menu={{
                  items: options?.map((el) => {
                    if (el !== current_status)
                      return {
                        key: el,
                        label: (
                          <Flex gap={8} align="center">
                            <DotsComponent status={`${statusMap[el]}`} />
                            <Text>{statusTextMap[el] || el}</Text>
                          </Flex>
                        )
                      }
                  }),
                  onClick: ({ key }) => {
                    if (key === current_status) return
                    onChangeStatusDescriptor({
                      enrollment: enrollment?.name?.toLowerCase(),
                      enrollment_id: enrollment.id,
                      billing_descriptor,
                      newStatus: key
                    })
                  }
                }}
              >
                <Flex gap={8} align="center">
                  <Text strong underline={true}>
                    {statusTextMap[current_status] || current_status}
                  </Text>
                  <Icon type="chevron_down_small" style={{ marginLeft: -4, marginRight: -4 }} />
                </Flex>
              </Dropdown>

              <Divider type="vertical" style={{ margin: "0 4px" }} />

              <Dropdown
                overlayStyle={{
                  maxHeight: 200,
                  overflow: "auto",
                  borderRadius: 15,
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
                }}
                overlayClassName="custom-dropdown"
                menu={{
                  items: options_type?.map((el) => ({
                    key: el,
                    label: (
                      <Flex gap={8} align="center">
                        <Text>{el}</Text>
                      </Flex>
                    )
                  })),
                  onClick: ({ key }) => {
                    if (key === descriptorData?.descriptor_type) return
                    const data = {
                      billing_descriptor,
                      descriptor_type: key
                    }
                    openModalConfirm(data)
                  }
                }}
              >
                <Flex gap={8} align="center">
                  <Text strong underline={true}>
                    {descriptorData?.descriptor_type}
                  </Text>
                  <Icon type="chevron_down_small" style={{ marginLeft: -4, marginRight: -4 }} />
                </Flex>
              </Dropdown>
            </Flex>
            <Divider style={{ margin: "12px 0 0" }} />
          </div>
        </div>
      )
    } else {
      return (
        <div style={{ ...style, display: "flex", alignItems: "flex-start" }}>
          <div style={{ margin: 12, width: "100%" }}>
            <Tooltip
              placement={"topLeft"}
              arrow={false}
              title={
                current_status ? (
                  <Text strong style={{ color: "#ffffff", fontSize: 12 }}>
                    {t(tooltipTextMap[current_status])}
                  </Text>
                ) : null
              }
            >
              <div>
                <Text>
                  <Private>{billing_descriptor}</Private>
                </Text>
                <Flex gap={8} align="center">
                  <DotsComponent status={`${statusMap[current_status]}`} />
                  <Text type="secondary" style={{ textDecoration: "underline dotted 10%" }}>
                    {statusTextMap[current_status] || current_status}
                  </Text>
                  <Divider type="vertical" style={{ margin: "0 4px" }} />
                  <Text type="secondary">{descriptorData?.descriptor_type}</Text>
                </Flex>
              </div>
            </Tooltip>
            <Divider style={{ margin: "12px 0 0" }} />
          </div>
        </div>
      )
    }
  }

  // Check height
  const estimateItemSize = (index, loadedDescriptors) => {
    const descriptor = loadedDescriptors[index]
    if (!descriptor) return 60

    const text = descriptor.billing_descriptor || ""

    // More accurate calculation
    // 1. We take into account the actual transfer of lines
    const newlines = (text.match(/\n/g) || []).length

    // 2. Calculation by the number of characters (30-34 per line)
    const charsPerLine = 32
    const linesByChars = Math.ceil(text.length / charsPerLine)

    // 3. We take the maximum value
    const totalLines = Math.max(newlines + 1, linesByChars, 1)

    // 4. Basic height + additional height per line
    const baseHeight = 60

    if (totalLines <= 1) {
      return baseHeight
    } else if (totalLines === 2) {
      return 80
    } else {
      return 60 + (totalLines - 1) * 18 // 18px for each additional line
    }
  }

  const getItemSize = (index) => estimateItemSize(index, loadedDescriptors)

  return (
    <div
      className={cls("enrollment-descriptors-status-card", {
        "enrollment-descriptors-status-card_admin": isAdmin
      })}
    >
      <div className={"enrollment-descriptors-status-card-header"}>
        <Text type="secondary">{t("Descriptor")}</Text>
      </div>
      <div
        className={cls("enrollment-descriptors-status-card-body", {
          empty: !enrollment["is_active"]
        })}
      >
        {enrollment["is_active"] ? (
          <>
            {/* Virtual list */}
            <List
              height={400}
              itemCount={loadedDescriptors?.length}
              itemSize={getItemSize}
              width="100%"
              className={cls("custom-react-window-list", {
                "no-has-more": !hasMore
              })}
            >
              {DescriptorRow}
            </List>

            {hasMore && (
              <Flex justify="center" style={{ padding: "12px 0 6px" }}>
                <Button
                  // type="primary"
                  loading={isLoading}
                  onClick={loadMore}
                >
                  {t("Load more")} ({loadedDescriptors?.length} / {filteredDescriptors?.length})
                </Button>
              </Flex>
            )}
          </>
        ) : (
          <>
            <Text type="secondary" strong>
              {t("No enrollments connected yet")}
            </Text>
            <Text
              type="secondary"
              style={{
                textAlign: "center",
                display: "inline-block",
                maxWidth: 202
              }}
            >
              {t("If you wish to use _enrollment_, add one or more enrollments.", {
                enrollment: enrollment.name
              })}
            </Text>
          </>
        )}
      </div>
    </div>
  )
}
export default EnrollmentDescriptorCard
